import type { IEventService } from '@realitycollective/service-framework';
import { createServiceToken } from '@realitycollective/service-framework';
import type { StrokeMessage } from '../../protocol/strokeMessages';

/**
 * One validated stroke message, ready to render (Plan-04 §3.2): parsed,
 * sender-verified, points converted to VIEWPORT space (the same invert flags
 * the detection path applies), and the capture-time frame resolved.
 *
 * Web divergence from the Unity `StrokeArrival`: instead of a resolved pose
 * snapshot, this carries `poseArrivalMs` - the local arrival time of the
 * frame the stroke was drawn on (by `ptsRef` against the detections arrival
 * history when possible, else the stroke's own arrival - RA-D6's fallback).
 * The app's render system runs the IDENTICAL latency-compensated
 * `PoseHistory` lookup the box renderer runs, so the library stays free of
 * engine pose types. Null = not a begin/end (appends reuse their stroke's
 * frozen pose downstream).
 */
export interface StrokeArrival {
  readonly message: StrokeMessage;
  /** Transport identity of the publisher - the attribution truth (RA-D2). */
  readonly senderIdentity: string;
  readonly arrivalTimeMs: number;
  readonly poseArrivalMs: number | null;
  /** `ptsRef` was present but no longer matched the arrival history (rendered via the fallback). */
  readonly late: boolean;
}

/**
 * The renderer's half of the stroke health picture (RA-D4): what actually
 * reached the wearer's VIEW, as opposed to what reached the device.
 */
export interface StrokeRenderCounters {
  /** Strokes currently drawn in the wearer's view. */
  readonly live: number;
  /** Strokes discarded at placement: no capture pose matched. */
  readonly unplaced: number;
  /** Events for strokes already expired or erased - appends outrunning their own stroke's TTL. */
  readonly stale: number;
}

export type AssistStrokeEventMap = {
  /** A validated stroke message arrived (or a local clear-all was requested). */
  strokeReceived: StrokeArrival;
};

/**
 * The wearer-side `strokes` topic consumer (Plan-04): validates and
 * bounds-checks assistants' stroke messages off the room's data events and
 * hands them - frame-resolved - to whatever renders them. Port of the Unity
 * `IAssistStrokeService`.
 */
export interface IAssistStrokeService extends IEventService<AssistStrokeEventMap> {
  /**
   * The wearer's "Clear drawings" action (§3.2): broadcasts `clear/all` on the
   * strokes topic AND raises `strokeReceived` locally (publishes do not loop back).
   */
  publishClearAll(): void;

  /** Mirror of the detection path's invert flags - stroke points share the bbox wire convention. */
  invertY: boolean;
  invertX: boolean;

  /** Validated stroke messages accepted this session. */
  readonly receivedCount: number;
  /** Messages dropped: malformed, unauthorized sender, or over the per-sender rate cap (RA-D4). */
  readonly droppedCount: number;
  /** Strokes whose `ptsRef` no longer matched the arrival history - the "late" signal. */
  readonly lateCount: number;

  /** Register the renderer's counter source (RA-D4 health surface). Null clears it. */
  setRenderCounterSource(source: (() => StrokeRenderCounters) | null): void;

  /** The renderer's latest counters, sampled on demand. All zero when nothing renders strokes. */
  readonly renderCounters: StrokeRenderCounters;
}

export const IAssistStrokeService = createServiceToken<IAssistStrokeService>('IAssistStrokeService');
